import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { connect } from "react-redux";
import { Dispatch } from "redux";

import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { ProductChildrenView } from './product-children.view';
import { ProductDataService } from '../product-form.data';
import { ContextService } from 'src/core/services/context.service';
import { actionCreators } from "src/store/products/product-form-nav-actions";

interface PropsFromState {
	childrenProducts?: ProductItem[];
	category: ProductCategorySummary;
}

interface PropsFromDispatch {
	navigateToChild: (
		child: ProductItem,
		title: string,
		text: string,
		key: string,
		product: ProductItem,
		defaultLanguage: string,
	) => any;
	childrenLoaded: (product: ProductItem, childrenProducts: ProductItem[]) => any;
}

interface Props extends ComponentViewProps<ProductItem> {
	mode: ControlMode;
	contextService: ContextService;
	onModeChanged: (mode: ControlMode) => void;
	defaultLanguage: string;
}

@observer
class ProductChildrenController extends React.Component<Props & PropsFromDispatch & PropsFromState, {}> {
	@resolve
	toast: ToastNotifications;

	@observable
	dataService: ProductDataService;

	componentDidMount() {
		this.loadData();
	}

	private async loadData(): Promise<void> {
		const { model, category, childrenProducts, setBusy, childrenLoaded, contextService } = this.props;

		this.dataService = ProductDataService.make(contextService);

		const childrenOfType = category.canHaveChildrenOfType;
		if ((childrenProducts == null || childrenProducts.length === 0) && model.id) {
			this.dataService.addFilter('isDeleted', 'eq', false);
			this.dataService.addFilter('parentProductId', 'eq', model.id);
			this.dataService.addFilter('productType', 'eq', childrenOfType);
			this.dataService.addSorting('id', 'asc', true);
			this.dataService.pageSize = 25;
			try {
				setBusy(true);
				const items = await this.dataService.load(query => query);
				childrenLoaded(model, items.slice());
			} catch (error) {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			} finally {
				setBusy(false);
			}
		} else {
			this.dataService.items = childrenProducts.filter(e => e.productType == childrenOfType);
		}
	}

	handleItemDoubleClick = async (item: ProductItem): Promise<void> => {
		const { model, defaultLanguage, navigateToChild, category, childrenLoaded } = this.props;
		const errors = await model.validate(false);
		if (errors.length != 0) {
			this.toast.showToast('Validation error!', errors.map(o => o.text), 'error');
			return;
		}

		if (item == null) {
			item = await this.dataService.add(category.canHaveChildrenOfTypeText, defaultLanguage, model);
			childrenLoaded(model, [item]);
		}
		const title = category.canHaveChildrenOfTypeText;
		const text = item.dynamicProperties['displayName'][defaultLanguage] || 'New';
		navigateToChild(item, title, text, 'sub-product', model, defaultLanguage);
	};

	private getContractColumn = (item: ProductItem) => {
		if (item.contracts) {
			let res = item.contracts.map(c => c.dynamicProperties.displayName[this.props.defaultLanguage]).join(" , ");
			return res;
		}
		else if (item.contractsDisplayNames) {
			let res = item.contractsDisplayNames.join(" , ");
			return res;
		}
		return "";
	}

	render() {
		if (this.dataService == null) {
			return null;
		}
		return (
			<ProductChildrenView
				getContractColumn={this.getContractColumn}
				mode={this.props.mode}
				model={this.dataService}
				setBusy={this.props.setBusy}
				onItemDoubleClicked={this.handleItemDoubleClick}
			/>
		);
	}
}

const mapStateToProps = (
	state: IApplicationState,
	props: ComponentViewProps<ProductItem>
): PropsFromState => {
	const product = state.otherLoadedEntities.find(e => e.entity == props.model.entity) as ProductItem;
	return {
		childrenProducts: product.childrenProducts
			? product.childrenProducts.filter(e => e.productType == product.category.canHaveChildrenOfType)
			: undefined,
		category: product.category,
	}
};

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
	navigateToChild: (
		entity,
		title: string,
		text: string,
		key: string,
		product: ProductItem,
		defaultLanguage: string,
	) => dispatch(actionCreators.productFormUpNav(entity, title, text, key, {
		entity: product,
		key: "product",
		title: "Product",
		text: product.dynamicProperties['displayName'][defaultLanguage],
	})),
	childrenLoaded: (
		product: ProductItem,
		childrenProducts: ProductItem[]
	) => dispatch(actionCreators.productFormRelationshipLoaded(product, childrenProducts, "childrenProducts")),
});

const connected = connect(mapStateToProps, mapDispatchToProps)(ProductChildrenController);
export { connected as ProductChildrenController };



// WEBPACK FOOTER //
// ./src/app/products/products/form/parts/product-children.controller.tsx